/**
 * LilaLand Photo Upload Core
 *
 * Shared upload pipeline used by:
 *   - assets/product-wizard.js (multi-slot dropzones on product pages)
 *   - assets/photo-to-art.js   (single-slot lead-capture tool)
 *
 * Pipeline:
 *   snapshot  → magic-byte validate → HEIC→JPEG (lazy) →
 *   downscale under 1000 KB (lazy) → POST with retry
 *
 * Backend constraint: /api/photos/upload accepts JPEG/PNG/WebP/AVIF and
 * rejects anything > 10 MB at the multipart parser. We enforce the type
 * client-side so the user gets a precise, actionable error instead of a
 * generic server rejection (and so we never even attempt to upload an
 * obviously bad file).
 *
 * Resolution policy: every photo is normalized before upload so downstream
 * consumers (AI generation + on-screen preview) can assume a sane size. We
 * scale proportionally (NEVER crop) and drop quality until the file is under
 * MAX_UPLOAD_BYTES (1000 KB) and MAX_DIMENSION (2048 px) on the long edge,
 * re-encoding to JPEG (PNG sources stay PNG). Files that are already web-
 * friendly AND already under 1000 KB pass through untouched — no point
 * re-encoding something that's already small.
 *
 * Errors are always UploadError instances with:
 *   { code, userMessage, technical, retryable }
 * `userMessage` is one sentence: WHAT happened + HOW to fix. Surfaces should
 * display it verbatim in red text.
 */
(function () {
  "use strict";

  if (window.InstaMePhotoUpload) return;

  // ── Constants ───────────────────────────────────────────────────────

  // Backend hard limit at the multipart parser. Our own target is far smaller
  // (MAX_UPLOAD_BYTES below); this stays only as the absolute backstop.
  const MAX_BYTES = 10 * 1024 * 1024;

  // Normalization targets, applied to every photo before upload. We never
  // crop — only scale the long edge down proportionally and lower quality
  // until the file fits. Small enough for AI generation + on-screen preview,
  // but not so small the pet looks soft.
  const MAX_UPLOAD_BYTES = 1000 * 1024; // hard ceiling we keep every upload under
  const TARGET_SIZE_MB = 0.95; // compressor byte target (~972 KB), safely under the ceiling
  const MAX_DIMENSION = 1536; // cap on the long edge — matches the AI's max output size; retina-safe for display

  // Sent as-is to the backend (which accepts these four). Anything else gets
  // transcoded to JPEG before upload.
  const WEB_FRIENDLY = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/avif",
  ]);

  // Heavy libs — lazy-loaded only when we actually need them. heic-to is
  // only loaded when we get a HEIC file; browser-image-compression is only
  // loaded when we need to shrink something.
  const HEIC_TO_URL =
    "https://cdn.jsdelivr.net/npm/heic-to@1.4.2/dist/iife/heic-to.js";
  const IMG_COMPRESS_URL =
    "https://cdn.jsdelivr.net/npm/browser-image-compression@2.0.2/dist/browser-image-compression.js";

  // 3 attempts total: immediate, +1s, +2s. Only retryable errors (network,
  // 5xx, 408, 429) re-attempt; 4xx errors fail fast.
  const RETRY_DELAYS_MS = [1000, 2000];

  // ── Error type ──────────────────────────────────────────────────────

  class UploadError extends Error {
    constructor({ code, userMessage, technical, retryable, cause }) {
      super(userMessage || code);
      this.name = "UploadError";
      this.code = code;
      this.userMessage = userMessage;
      this.technical = technical || userMessage || code;
      this.retryable = !!retryable;
      if (cause) this.cause = cause;
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  function getBackendUrl() {
    return (/(^|\.)(instame\.shop|lilaland\.co|lilaland\.pet)$/i.test(location.hostname) || /\.myshopify\.com$/i.test(location.hostname))
      ? "https://api.lilaland.co"
      : (location.hostname === "localhost" || location.hostname === "127.0.0.1")
        ? "http://localhost:3000" // direct `shopify theme dev` → local backend
        : ""; // other non-prod hosts (Tailscale/phone) → same-origin dev gateway
  }

  function isWebFriendly(mime) {
    return WEB_FRIENDLY.has(mime);
  }

  function fmtMB(bytes) {
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  }

  function lazyLoadScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === "true") return resolve();
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error(`Failed to load ${src}`)),
        );
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      // Without this, any uncaught throw INSIDE these cross-origin bundles
      // reaches window.onerror sanitized to a bare "Script error." with no
      // message, file or stack — and both of them (HEIC decode, image
      // downscale) sit on the photo-upload critical path, on exactly the
      // low-end phones where a decode or OOM failure is most likely. That
      // made real upload failures indistinguishable from in-app-browser
      // noise in error tracking. Both CDN responses send
      // `access-control-allow-origin: *` (verified), so requesting CORS here
      // is free and cannot break the load.
      script.crossOrigin = "anonymous";
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = "true";
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  function sleep(ms, signal) {
    return new Promise((resolve, reject) => {
      const t = setTimeout(resolve, ms);
      if (signal) {
        const onAbort = () => {
          clearTimeout(t);
          reject(
            new UploadError({
              code: "ABORTED",
              userMessage: "Upload cancelled.",
              technical: "Aborted via signal during retry backoff.",
            }),
          );
        };
        if (signal.aborted) onAbort();
        else signal.addEventListener("abort", onAbort, { once: true });
      }
    });
  }

  // ── Magic-byte detection ────────────────────────────────────────────
  //
  // file.type from the browser is unreliable: empty for many HEIC files on
  // iOS, sometimes wrong for renamed files. Reading the first ~32 bytes and
  // checking signatures is authoritative and fast (no full file load).

  async function readHeader(file, n = 32) {
    const slice = file.slice(0, n);
    const ab = await slice.arrayBuffer();
    return new Uint8Array(ab);
  }

  function bytesEq(bytes, offset, sig) {
    for (let i = 0; i < sig.length; i++) {
      if (bytes[offset + i] !== sig[i]) return false;
    }
    return true;
  }

  function asciiAt(bytes, offset, len) {
    let s = "";
    for (let i = 0; i < len; i++) {
      const c = bytes[offset + i];
      if (c == null) return s;
      s += String.fromCharCode(c);
    }
    return s;
  }

  /**
   * Returns { kind, mime, ext } where:
   *   kind: 'image' | 'video' | 'audio' | 'pdf' | 'archive' | 'unknown'
   *   mime: best-guess MIME, or null
   *   ext:  conventional extension, or null
   */
  async function detectFileType(file) {
    let header;
    try {
      header = await readHeader(file, 32);
    } catch (err) {
      throw new UploadError({
        code: "READ_FAILED",
        userMessage:
          "We couldn't read this photo from disk. If it's a fresh screenshot, save it first, then upload it.",
        technical: `readHeader failed: ${err && err.message}`,
        cause: err,
      });
    }
    if (header.length < 4) return { kind: "unknown", mime: null, ext: null };

    if (bytesEq(header, 0, [0xff, 0xd8, 0xff]))
      return { kind: "image", mime: "image/jpeg", ext: "jpg" };
    if (
      bytesEq(header, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
    )
      return { kind: "image", mime: "image/png", ext: "png" };
    if (bytesEq(header, 0, [0x47, 0x49, 0x46, 0x38]))
      return { kind: "image", mime: "image/gif", ext: "gif" };
    if (bytesEq(header, 0, [0x42, 0x4d]))
      return { kind: "image", mime: "image/bmp", ext: "bmp" };

    // RIFF container: WebP, AVI, WAV all share the same first 4 bytes.
    if (asciiAt(header, 0, 4) === "RIFF") {
      const fourcc = asciiAt(header, 8, 4);
      if (fourcc === "WEBP")
        return { kind: "image", mime: "image/webp", ext: "webp" };
      if (fourcc === "AVI ")
        return { kind: "video", mime: "video/x-msvideo", ext: "avi" };
      if (fourcc === "WAVE")
        return { kind: "audio", mime: "audio/wav", ext: "wav" };
      return { kind: "unknown", mime: null, ext: null };
    }

    // ISOBMFF ("ftyp" box at bytes 4-7): HEIC, AVIF, MP4, MOV all share this.
    if (asciiAt(header, 4, 4) === "ftyp") {
      const brand = asciiAt(header, 8, 4).toLowerCase();
      if (
        [
          "heic",
          "heix",
          "hevc",
          "hevx",
          "heim",
          "heis",
          "hevm",
          "hevs",
          "mif1",
          "msf1",
        ].includes(brand)
      ) {
        return { kind: "image", mime: "image/heic", ext: "heic" };
      }
      if (brand === "avif" || brand === "avis") {
        return { kind: "image", mime: "image/avif", ext: "avif" };
      }
      if (brand === "qt  ") {
        return { kind: "video", mime: "video/quicktime", ext: "mov" };
      }
      // Anything else with ftyp box — treat as video (mp4, m4v, 3gp, etc.).
      return { kind: "video", mime: "video/mp4", ext: "mp4" };
    }

    // WebM / Matroska
    if (bytesEq(header, 0, [0x1a, 0x45, 0xdf, 0xa3]))
      return { kind: "video", mime: "video/webm", ext: "webm" };
    // PDF
    if (bytesEq(header, 0, [0x25, 0x50, 0x44, 0x46]))
      return { kind: "pdf", mime: "application/pdf", ext: "pdf" };
    // ZIP (also docx/xlsx/pptx)
    if (header[0] === 0x50 && header[1] === 0x4b)
      return { kind: "archive", mime: "application/zip", ext: "zip" };
    // MP3 (ID3v2 tag or MPEG audio frame sync)
    if (
      asciiAt(header, 0, 3) === "ID3" ||
      (header[0] === 0xff && (header[1] & 0xe0) === 0xe0)
    ) {
      return { kind: "audio", mime: "audio/mpeg", ext: "mp3" };
    }
    // TIFF (little- and big-endian)
    if (
      bytesEq(header, 0, [0x49, 0x49, 0x2a, 0x00]) ||
      bytesEq(header, 0, [0x4d, 0x4d, 0x00, 0x2a])
    ) {
      return { kind: "image", mime: "image/tiff", ext: "tiff" };
    }

    return { kind: "unknown", mime: null, ext: null };
  }

  // ── Snapshot ────────────────────────────────────────────────────────
  //
  // Read the file's bytes into memory and wrap them in a fresh File. A File
  // object is just a path reference; for macOS screenshots dragged from the
  // floating preview, the OS moves the temp file out from under us within a
  // few seconds, breaking any deferred read. Snapshotting now detaches us
  // from the OS-side lifecycle.
  //
  // MUST be called as the FIRST awaited step after the change/drop event —
  // anything synchronous can run before, but no other awaits.

  async function snapshotFile(file) {
    let bytes;
    try {
      bytes = await file.arrayBuffer();
    } catch (err) {
      throw new UploadError({
        code: "READ_FAILED",
        userMessage:
          "We couldn't read this photo from disk. If it's a fresh screenshot, save it first, then upload it.",
        technical: `arrayBuffer failed for ${file.name}: ${err && err.message}`,
        cause: err,
      });
    }
    return new File([bytes], file.name || "photo.png", {
      type: file.type || "image/png",
    });
  }

  // ── Validation ──────────────────────────────────────────────────────

  function rejectByKind(kind, file) {
    const sizeNote = file.size ? ` (${fmtMB(file.size)})` : "";
    if (kind === "video") {
      throw new UploadError({
        code: "INVALID_TYPE_VIDEO",
        userMessage: `This is a video, not a photo${sizeNote}. Please upload a JPG, PNG, HEIC, or WebP image.`,
        technical: `Detected video by magic bytes (file ${file.name}, type "${file.type || "unknown"}")`,
      });
    }
    if (kind === "pdf") {
      throw new UploadError({
        code: "INVALID_TYPE_PDF",
        userMessage:
          "PDFs aren't supported here. Please upload a JPG, PNG, HEIC, or WebP image.",
        technical: `Detected PDF (file ${file.name})`,
      });
    }
    if (kind === "audio") {
      throw new UploadError({
        code: "INVALID_TYPE_AUDIO",
        userMessage:
          "This is an audio file, not a photo. Please upload a JPG, PNG, HEIC, or WebP image.",
        technical: `Detected audio (file ${file.name})`,
      });
    }
    if (kind === "archive") {
      throw new UploadError({
        code: "INVALID_TYPE_ARCHIVE",
        userMessage:
          "This is a zip/archive file, not a photo. Please upload a JPG, PNG, HEIC, or WebP image.",
        technical: `Detected archive (file ${file.name})`,
      });
    }
    throw new UploadError({
      code: "INVALID_TYPE_UNKNOWN",
      userMessage:
        "We can't read this file as an image. Please upload a JPG, PNG, HEIC, or WebP photo.",
      technical: `Magic-byte detection failed (file ${file.name}, type "${file.type || "unknown"}")`,
    });
  }

  // ── HEIC → JPEG ─────────────────────────────────────────────────────

  async function convertHeicToJpeg(file) {
    if (!window.HeicTo) {
      try {
        await lazyLoadScript(HEIC_TO_URL);
      } catch (err) {
        throw new UploadError({
          code: "HEIC_LIB_LOAD_FAILED",
          userMessage:
            "Couldn't load the HEIC converter. Try uploading a JPG or PNG instead.",
          technical: `heic-to script failed to load from ${HEIC_TO_URL}: ${err.message}`,
          cause: err,
        });
      }
    }
    let blob;
    try {
      blob = await window.HeicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.9,
      });
    } catch (err) {
      throw new UploadError({
        code: "HEIC_CONVERT_FAILED",
        userMessage:
          "We couldn't convert this HEIC photo. Open it on your phone, take a screenshot, then upload the screenshot.",
        technical: `heic-to threw: ${err && err.message}`,
        cause: err,
      });
    }
    const newName =
      (file.name || "photo").replace(/\.(heic|heif)$/i, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg" });
  }

  // ── Downscale / normalize ───────────────────────────────────────────

  /**
   * Normalize a photo to a web-safe, reasonably small file before upload.
   *
   * Returns the file untouched only when it's already web-friendly AND already
   * under MAX_UPLOAD_BYTES — no point re-encoding (and degrading) something
   * that's already small. Everything else is scaled down proportionally
   * (NEVER cropped) and re-encoded until it fits, so downstream consumers (AI
   * generation, preview display) can assume a sane size without re-checking.
   *
   * The byte target + long-edge cap are both handed to the compressor, which
   * runs in a web worker (no UI freeze) and always preserves aspect ratio.
   */
  async function downscaleForUpload(file, { signal } = {}) {
    if (WEB_FRIENDLY.has(file.type) && file.size <= MAX_UPLOAD_BYTES) {
      return { file, wasCompressed: false };
    }

    if (!window.imageCompression) {
      try {
        await lazyLoadScript(IMG_COMPRESS_URL);
      } catch (err) {
        throw new UploadError({
          code: "COMPRESS_LIB_LOAD_FAILED",
          userMessage:
            "Couldn't load the image processor (network blocked?). Try a smaller photo or check your connection.",
          technical: `browser-image-compression failed to load: ${err.message}`,
          cause: err,
        });
      }
    }

    // Preserve PNG sources as PNG (a transparent background would turn black
    // if forced to JPEG); everything else becomes JPEG, far smaller for photos.
    const targetType = file.type === "image/png" ? "image/png" : "image/jpeg";
    let compressed;
    try {
      compressed = await window.imageCompression(file, {
        maxSizeMB: TARGET_SIZE_MB,
        maxWidthOrHeight: MAX_DIMENSION,
        useWebWorker: true,
        initialQuality: 0.8,
        fileType: targetType,
        signal,
      });
    } catch (err) {
      if (err && (err.name === "AbortError" || err.message === "AbortError")) {
        throw new UploadError({
          code: "ABORTED",
          userMessage: "Upload cancelled.",
          technical: "Compression aborted via signal.",
          cause: err,
        });
      }
      throw new UploadError({
        code: "COMPRESS_FAILED",
        userMessage: `This photo is ${fmtMB(file.size)} and we couldn't process it. Try exporting a JPG from your photo app first.`,
        technical: `browser-image-compression threw: ${err && err.message}`,
        cause: err,
      });
    }

    const ext = compressed.type === "image/png" ? ".png" : ".jpg";
    const newName = (file.name || "photo").replace(/\.(\w+)$/i, "") + ext;
    const out = new File([compressed], newName, {
      type: compressed.type || "image/jpeg",
    });

    // The compressor targets TARGET_SIZE_MB but can overshoot on pathological
    // inputs. A slight overshoot past 1000 KB is fine (the goal is "small,"
    // not exact) — only treat it as a failure if we're still over the backend's
    // hard 10 MB limit.
    if (out.size > MAX_BYTES) {
      throw new UploadError({
        code: "COMPRESS_FAILED",
        userMessage: `This photo is ${fmtMB(file.size)} and we couldn't shrink it enough. Try exporting a JPG from your photo app first.`,
        technical: `Output still ${fmtMB(out.size)} after compression`,
      });
    }
    return { file: out, wasCompressed: true };
  }

  // ── Upload with retry ───────────────────────────────────────────────

  function isRetryableStatus(status) {
    return status === 408 || status === 429 || (status >= 500 && status < 600);
  }

  async function postOnce({ file, userId, extraFields, signal, backendUrl }) {
    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("user_id", userId);
    if (extraFields) {
      for (const [k, v] of Object.entries(extraFields)) {
        if (v != null) formData.append(k, String(v));
      }
    }

    let response;
    try {
      response = await fetch(`${backendUrl}/api/photos/upload`, {
        method: "POST",
        body: formData,
        signal,
      });
    } catch (err) {
      if (err && err.name === "AbortError") {
        throw new UploadError({
          code: "ABORTED",
          userMessage: "Upload cancelled.",
          technical: "Fetch aborted via signal.",
          cause: err,
        });
      }
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        throw new UploadError({
          code: "OFFLINE",
          userMessage:
            "You're offline. Reconnect to Wi-Fi or cellular and try again.",
          technical: `fetch threw while offline: ${err && err.message}`,
          retryable: true,
          cause: err,
        });
      }
      throw new UploadError({
        code: "NETWORK_ERROR",
        userMessage:
          "The upload couldn't reach our server. Check your connection and try again.",
        technical: `fetch threw: ${err && err.message}`,
        retryable: true,
        cause: err,
      });
    }

    if (!response.ok) {
      let body;
      try {
        body = await response.json();
      } catch (_) {
        body = {};
      }
      const serverMessage = (body && body.error) || `HTTP ${response.status}`;
      const retryable = isRetryableStatus(response.status);
      const code = response.status >= 500 ? "SERVER_5XX" : "SERVER_4XX";
      const userMessage =
        response.status >= 500
          ? `Our server hit an error: ${serverMessage}. Please try again in a minute.`
          : `Server rejected this photo: ${serverMessage}.`;
      throw new UploadError({
        code,
        userMessage,
        technical: `POST /api/photos/upload → ${response.status} ${serverMessage}`,
        retryable,
      });
    }

    return await response.json();
  }

  async function uploadFile(file, opts = {}) {
    const userId = opts.userId;
    const extraFields = opts.extraFields || {};
    const signal = opts.signal;
    const backendUrl = opts.backendUrl || getBackendUrl();

    if (!userId) {
      throw new UploadError({
        code: "MISSING_USER_ID",
        userMessage:
          "We're missing your session ID. Refresh the page and try again.",
        technical: "uploadFile called without userId",
      });
    }

    const attempts = RETRY_DELAYS_MS.length + 1;
    let lastError;
    for (let i = 0; i < attempts; i++) {
      try {
        const data = await postOnce({
          file,
          userId,
          extraFields,
          signal,
          backendUrl,
        });
        return {
          upload_id: data.upload_id,
          photo_url: data.photo_url,
          // storage_path comes straight from the backend — no hardcoded
          // Supabase URL parsing on the client.
          photo_path: data.storage_path || data.photo_url,
        };
      } catch (err) {
        lastError = err;
        if (err instanceof UploadError && err.code === "ABORTED") throw err;
        const retryable = err instanceof UploadError && err.retryable;
        if (!retryable || i === attempts - 1) throw err;
        console.warn(
          `[photo-upload-core] attempt ${i + 1}/${attempts} failed (${err.code}), retrying in ${RETRY_DELAYS_MS[i]}ms:`,
          err.technical,
        );
        await sleep(RETRY_DELAYS_MS[i], signal);
      }
    }
    throw lastError;
  }

  // ── Pipeline ────────────────────────────────────────────────────────

  /**
   * Snapshot + validate + HEIC convert + compress. Returns a clean File
   * ready to upload, plus metadata about what was done. Use when you want
   * to defer the actual upload (e.g. show a preview, wait for the user to
   * click Continue).
   */
  async function prepareFile(file, _opts = {}) {
    const original = file;
    const snapshot = await snapshotFile(file);
    const detected = await detectFileType(snapshot);

    if (detected.kind !== "image") {
      rejectByKind(detected.kind, snapshot);
    }

    let prepped = snapshot;
    let wasConverted = false;
    if (
      detected.mime === "image/heic" ||
      /\.(heic|heif)$/i.test(snapshot.name)
    ) {
      prepped = await convertHeicToJpeg(snapshot);
      wasConverted = true;
    }

    const { file: fitted, wasCompressed } = await downscaleForUpload(prepped);

    return {
      file: fitted,
      wasConverted,
      wasCompressed,
      detectedMime: detected.mime,
      originalSize: original.size,
      finalSize: fitted.size,
    };
  }

  /**
   * Full pipeline: prepare + upload. Most callers want this. Surfaces that
   * defer upload (e.g. show preview, then upload on Continue) should call
   * prepareFile + uploadFile separately.
   */
  async function processAndUpload(file, opts = {}) {
    const prep = await prepareFile(file, opts);
    const result = await uploadFile(prep.file, opts);
    return {
      ...result,
      processedFile: prep.file,
      wasConverted: prep.wasConverted,
      wasCompressed: prep.wasCompressed,
      originalSize: prep.originalSize,
      finalSize: prep.finalSize,
      detectedMime: prep.detectedMime,
    };
  }

  // ── Delete (fire-and-forget) ────────────────────────────────────────

  /**
   * Hard-delete a previously-uploaded photo (DB row + Supabase Storage
   * object). Fire-and-forget — never throws, never blocks the caller.
   * The user shouldn't have to wait for the network round-trip just
   * because they replaced or removed a photo.
   *
   * `keepalive: true` lets the request survive a page navigation, so
   * deletes triggered right before the user leaves the page still go
   * through.
   */
  function deleteUpload(uploadId, opts) {
    if (!uploadId) return;
    opts = opts || {};
    const userId = opts.userId;
    if (!userId) {
      console.warn(
        "[photo-upload-core] deleteUpload skipped: no userId provided",
      );
      return;
    }
    const backendUrl = opts.backendUrl || getBackendUrl();
    try {
      fetch(`${backendUrl}/api/photos/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ upload_id: uploadId, user_id: userId }),
        keepalive: true,
      }).catch((err) => {
        console.warn(
          "[photo-upload-core] deleteUpload network error (ignored):",
          err && err.message,
        );
      });
    } catch (err) {
      console.warn(
        "[photo-upload-core] deleteUpload threw (ignored):",
        err && err.message,
      );
    }
  }

  window.InstaMePhotoUpload = {
    UploadError,
    MAX_BYTES,
    MAX_UPLOAD_BYTES,
    MAX_DIMENSION,
    isWebFriendly,
    getBackendUrl,
    detectFileType,
    snapshotFile,
    convertHeicToJpeg,
    downscaleForUpload,
    uploadFile,
    prepareFile,
    processAndUpload,
    deleteUpload,
  };
})();
